import { orderBy } from 'lodash';
import { SortedUser, UserKind } from '../sortedUser.interfaces';
import { UserListSorting } from '../userPreferences.interfaces';

export const mapAnyUserModelToPerson = (users: UserKind[]): SortedUser[] =>
  users.map(user => ({
    displayName: user.displayName ?? '',
    firstName: user.firstName ?? user.givenName ?? '',
    id: user.id ?? user.azureId ?? '',
    lastName: user.lastName ?? user.surname ?? '',
    mailAddress: user.userPrincipalName ?? user.mail ?? '',
    userRole: user.userRole ?? user.role ?? '',
    status: user.status,
    actions: user.actions,
  }));

export const getSortedUserLabel = (
  user: UserKind,
  sortMode: UserListSorting,
): string =>
  sortMode === UserListSorting.FirstName
    ? `${user.firstName ?? user.givenName} ${user.lastName ?? user.surname}`
    : `${user.lastName ?? user.surname}, ${user.firstName ?? user.givenName}`;

/**
 * Sorts a list of users by the user's name-display preference.
 *
 * Honors {@link UserListSorting}: when set to `LastName`, items are ordered
 * by last name (falling back to `surname`), otherwise by first name (falling
 * back to `givenName`). Comparison is case-insensitive.
 *
 * For flat lists of users, call with just `(users, sortMode)`. For lists whose
 * items wrap the user (e.g. `{ student: UserKind }`), pass a `selectUser`
 * accessor to extract the user from each item.
 *
 * Note: this orders the list — for formatting a single user's name label use
 * {@link getSortedUserLabel}.
 *
 * @param users - The items to sort. Original array is not mutated.
 * @param sortMode - The active name-sorting preference.
 * @param selectUser - Optional accessor extracting the {@link UserKind} from an item.
 * @returns A new array sorted according to the preference.
 */
export function sortUsersByPreference<T extends UserKind>(
  users: T[],
  sortMode: UserListSorting,
): T[];
export function sortUsersByPreference<T>(
  users: T[],
  sortMode: UserListSorting,
  selectUser: (item: T) => UserKind,
): T[];
export function sortUsersByPreference<T>(
  users: T[],
  sortMode: UserListSorting,
  selectUser: (item: T) => UserKind = item => item as unknown as UserKind,
): T[] {
  return orderBy(users, item => {
    const user = selectUser(item);
    return (sortMode === UserListSorting.LastName
      ? user.lastName ?? user.surname
      : user.firstName ?? user.givenName
    )?.toLowerCase();
  });
}
